import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { carsAdminService } from '../services/cars-admin.service'

export const ADMIN_CARS_KEYS = {
  overview: ['admin', 'cars', 'overview'] as const,
  brands: ['admin', 'cars', 'brands'] as const,
  models: (brandId?: number) => ['admin', 'cars', 'models', brandId] as const,
  generations: (modelId?: number) => ['admin', 'cars', 'generations', modelId] as const,
  variants: (generationId?: number) => ['admin', 'cars', 'variants', generationId] as const,
  features: ['admin', 'cars', 'features'] as const,
  listings: (status?: string) => ['admin', 'cars', 'listings', status] as const,
  modelRequests: ['admin', 'cars', 'model-requests'] as const,
}

// Overview
export function useAdminCarsOverview() {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.overview,
    queryFn: () => carsAdminService.getOverviewStats(),
  })
}

// Brands
export function useAdminCarBrands() {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.brands,
    queryFn: () => carsAdminService.getBrands(true),
  })
}

export function useCreateBrand() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string; logoUrl?: string; isFeatured?: boolean; displayOrder?: number }) =>
      carsAdminService.createBrand(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.brands })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Models
export function useAdminCarModels(brandId?: number) {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.models(brandId),
    queryFn: () => (brandId ? carsAdminService.getModelsByBrand(brandId) : Promise.resolve([])),
    enabled: !!brandId,
  })
}

export function useCreateModel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { brandId: number; name: string; bodyType?: string; displayOrder?: number }) =>
      carsAdminService.createModel(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.models(variables.brandId) })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Generations
export function useAdminCarGenerations(modelId?: number) {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.generations(modelId),
    queryFn: () => (modelId ? carsAdminService.getGenerationsByModel(modelId) : Promise.resolve([])),
    enabled: !!modelId,
  })
}

export function useCreateGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { modelId: number; name: string; startYear: number; endYear?: number }) =>
      carsAdminService.createGeneration(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.generations(variables.modelId) })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Variants
export function useAdminCarVariants(generationId?: number) {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.variants(generationId),
    queryFn: () => (generationId ? carsAdminService.getVariantsByGeneration(generationId) : Promise.resolve([])),
    enabled: !!generationId,
  })
}

export function useCreateVariant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      generationId: number
      name: string
      fuelType: string
      transmission: string
      engineCc?: number
      horsepower?: number
      torqueNm?: number
      mileageKmpl?: number
      seatingCapacity?: number
    }) => carsAdminService.createVariant(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.variants(variables.generationId) })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Features
export function useAdminCarFeatures() {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.features,
    queryFn: () => carsAdminService.getFeatures(),
  })
}

export function useCreateFeature() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { category: string; name: string; code: string; description?: string }) =>
      carsAdminService.createFeature(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.features })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Moderation
export function useAdminListings(status?: string) {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.listings(status),
    queryFn: () => carsAdminService.getAdminListings(status),
  })
}

export function useApproveListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsAdminService.approveListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'listings'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useRejectListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => carsAdminService.rejectListing(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'listings'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Model Requests
export function useAdminModelRequests() {
  return useQuery({
    queryKey: ADMIN_CARS_KEYS.modelRequests,
    queryFn: () => carsAdminService.getModelRequests(),
  })
}

export function useUpdateModelRequestStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'APPROVED' | 'REJECTED' }) =>
      carsAdminService.updateModelRequestStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.modelRequests })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

// Delete mutations
export function useDeleteBrand() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsAdminService.deleteBrand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.brands })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useDeleteModel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, brandId }: { id: number; brandId?: number }) => carsAdminService.deleteModel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'models'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useDeleteGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, modelId }: { id: number; modelId?: number }) => carsAdminService.deleteGeneration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useDeleteVariant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, generationId }: { id: number; generationId?: number }) => carsAdminService.deleteVariant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'variants'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useDeleteFeature() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsAdminService.deleteFeature(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.features })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}

export function useDeleteListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsAdminService.deleteListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'listings'] })
      queryClient.invalidateQueries({ queryKey: ADMIN_CARS_KEYS.overview })
    },
  })
}
